import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MOCK_SERVICES } from '../data';
import { imageUrl } from '../lib/images';

export default function OtherTasks() {
  const [services, setServices] = useState(MOCK_SERVICES.filter(s => s.category === 'other'));

  useEffect(() => {
    async function fetchServices() {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/services');
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setServices(data.filter(s => s.category === 'other'));
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    }
    fetchServices();
  }, []);

  return (
    <div className="pt-20">
      <Helmet>
        <title>Andre Grønne Opgaver – Græsslåning, Flishugning & Havearbejde | DK Grøn Service</title>
        <meta name="description" content="Få professionel hjælp til græsslåning, flishugning af haveaffald og havearbejde for private og erhverv. Fleksible løsninger udført med moderne maskiner." />
        <meta property="og:title" content="Andre Grønne Opgaver – Græsslåning, Flishugning & Havearbejde | DK Grøn Service" />
        <meta property="og:description" content="Få professionel hjælp til græsslåning, flishugning af haveaffald og havearbejde for private og erhverv. Fleksible løsninger udført med moderne maskiner." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <section className="bg-emerald-50 py-24">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-emerald-950 mb-6">Andre opgaver</h1>
          <p className="text-xl text-emerald-800/70">
            Udover vores faste ejendomsservice tilbyder vi en række specialiserede opgaver til både private og erhverv.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {services.map((service) => (
              <Link
                key={service.id}
                to={`/andre-opgaver/${service.url_slug}`}
                className="group flex flex-col"
              >
                <div className="aspect-[4/3] rounded-[2.5rem] overflow-hidden mb-8 shadow-lg">
                  <img
                    src={imageUrl(service.image[0], 'width=600,quality=80,format=auto')}
                    loading="lazy"
                    decoding="async"
                    alt={service.title.da}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h2 className="text-2xl font-bold text-emerald-950 mb-4 group-hover:text-emerald-600 transition-colors">
                  {service.title.da}
                </h2>
                <p className="text-emerald-800/60 leading-relaxed mb-6 flex-grow">
                  {service.description.da.replace(/<[^>]*>/g, '').slice(0, 150)}...
                </p>
                <div className="inline-flex items-center gap-2 text-emerald-600 font-bold group-hover:gap-3 transition-all">
                  Læs mere om {service.title.da.toLowerCase()}
                  <ArrowRight className="w-5 h-5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-emerald-950 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-900 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl opacity-50" />
        <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Har du en specifik opgave i tankerne?</h2>
          <p className="text-xl text-emerald-100/70 mb-12">
            Vi er altid klar på en udfordring. Kontakt os med din forespørgsel, så finder vi en løsning.
          </p>
          <Link
            to="/kontakt"
            className="inline-block bg-emerald-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-emerald-500 transition-all shadow-xl"
          >
            Kontakt os nu
          </Link>
        </div>
      </section>
    </div>
  );
}
